import {
	Popover,
	PopoverContent,
	PopoverMenu,
	PopoverMenuItem,
	PopoverTrigger,
	SwitchField,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { ConfirmDialog } from "molecules/dialog/ConfirmDialog";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
	IconAlertTriangle,
	IconPencil,
	IconPlus,
	IconSparkles,
	IconTrash,
	IconUpload,
	IconWriting,
} from "@tabler/icons-react";

import {
	useCreateSkillMutation,
	useDeleteSkillMutation,
	useGetSkillsQuery,
	useParseSkillUploadMutation,
	useUpdateSkillMutation,
} from "../../ai-chat/api/aiSkillsApi";
import type { SkillResponse } from "../../ai-chat/api/skillTypes";
import { MAX_SKILLS } from "../../ai-chat/api/skillTypes";
import { SkillFormDialog } from "./SkillFormDialog";

interface AiSkillsSectionProps {
	companyDomainId: string;
}

const CREATE_WITH_AI_PROMPT =
	"Let's create a skill together using your skill-creator skill. First ask me what the skill should do.";

export function AiSkillsSection({ companyDomainId }: AiSkillsSectionProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: skills = [] } = useGetSkillsQuery(companyDomainId);
	const [createSkill] = useCreateSkillMutation();
	const [updateSkill] = useUpdateSkillMutation();
	const [deleteSkill] = useDeleteSkillMutation();
	const [parseSkillUpload] = useParseSkillUploadMutation();

	const [formDialogOpen, setFormDialogOpen] = useState(false);
	const [editingSkill, setEditingSkill] = useState<SkillResponse | undefined>();
	const [deletingSkill, setDeletingSkill] = useState<SkillResponse | null>(
		null,
	);
	const fileInputRef = useRef<HTMLInputElement>(null);

	function handleEdit(skill: SkillResponse) {
		setEditingSkill(skill);
		setFormDialogOpen(true);
	}

	function handleWriteInstructions() {
		setEditingSkill(undefined);
		setFormDialogOpen(true);
	}

	function handleCreateWithAi() {
		navigate("../../ai-chat/conversations/new", {
			relative: "path",
			state: { prefillMessage: CREATE_WITH_AI_PROMPT },
		});
	}

	async function handleFileUpload(file: File) {
		try {
			const parsed = await parseSkillUpload({
				companyDomainId,
				file,
			}).unwrap();
			await createSkill({
				companyDomainId,
				name: parsed.name,
				description: parsed.description,
				instructions: parsed.instructions,
			}).unwrap();
			showSuccessNotification({ message: t("Skill created") });
		} catch {
			showErrorNotification({ message: t("Failed to upload skill") });
		}
	}

	function handleFormClose() {
		setFormDialogOpen(false);
		setEditingSkill(undefined);
	}

	async function handleToggleEnabled(skill: SkillResponse) {
		try {
			await updateSkill({
				companyDomainId,
				skillId: skill.id,
				isEnabled: !skill.isEnabled,
			}).unwrap();
		} catch {
			showErrorNotification({ message: t("Failed to update skill") });
		}
	}

	async function handleDelete() {
		if (!deletingSkill) return;
		try {
			await deleteSkill({
				companyDomainId,
				skillId: deletingSkill.id,
			}).unwrap();
			showSuccessNotification({ message: t("Skill deleted") });
			setDeletingSkill(null);
		} catch {
			showErrorNotification({ message: t("Failed to delete skill") });
		}
	}

	const isAtLimit = skills.length >= MAX_SKILLS;

	return (
		<div className="mt-6">
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<span className="font-bold text-lg">{t("Skills")}</span>
					<span className="text-sm text-gray-500">({skills.length})</span>
				</div>
				<Popover role="menu" placement="bottom-end">
					<PopoverTrigger asChild>
						<Button
							size="sm"
							icon={<IconPlus size={16} />}
							disabled={isAtLimit}
						>
							{t("Add skill")}
						</Button>
					</PopoverTrigger>
					<PopoverContent>
						<PopoverMenu>
							<PopoverMenuItem
								label={t("Create with {{aiProductName}}")}
								icon={<IconSparkles size={16} />}
								onClick={handleCreateWithAi}
							/>
							<PopoverMenuItem
								label={t("Write skill instructions")}
								icon={<IconWriting size={16} />}
								onClick={handleWriteInstructions}
							/>
							<PopoverMenuItem
								label={t("Upload a skill")}
								icon={<IconUpload size={16} />}
								onClick={() => fileInputRef.current?.click()}
							/>
						</PopoverMenu>
					</PopoverContent>
				</Popover>
				<input
					ref={fileInputRef}
					type="file"
					accept=".md"
					className="hidden"
					onChange={(e) => {
						const file = e.target.files?.[0];
						if (file) handleFileUpload(file);
						e.target.value = "";
					}}
				/>
			</div>

			{skills.length === 0 ? (
				<p className="text-sm text-gray-500">{t("No skills configured yet")}</p>
			) : (
				<div className="space-y-2">
					{skills.map((skill) => (
						<div
							key={skill.id}
							className="flex items-center gap-3 rounded-lg border border-gray-200 p-3"
						>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									<span className="font-medium text-sm truncate">
										{skill.name}
									</span>
									{skill.anthropicSkillId === null && (
										<Tooltip>
											<TooltipTrigger>
												<IconAlertTriangle
													size={16}
													className="text-amber-500 shrink-0"
												/>
											</TooltipTrigger>
											<TooltipContent>
												{t(
													"This skill failed to sync and will not be active in chat",
												)}
											</TooltipContent>
										</Tooltip>
									)}
								</div>
								<p className="text-xs text-gray-500 truncate">
									{skill.description}
								</p>
							</div>
							<SwitchField
								checked={skill.isEnabled}
								onChange={() => handleToggleEnabled(skill)}
							/>
							<Button
								variant="secondaryGray"
								size="sm"
								icon={<IconPencil size={16} />}
								onClick={() => handleEdit(skill)}
							>
								{t("Edit")}
							</Button>
							<Button
								variant="secondaryGray"
								size="sm"
								icon={<IconTrash size={16} />}
								destructive
								onClick={() => setDeletingSkill(skill)}
							>
								{t("Delete")}
							</Button>
						</div>
					))}
				</div>
			)}

			{formDialogOpen && (
				<SkillFormDialog
					open={formDialogOpen}
					onClose={handleFormClose}
					companyDomainId={companyDomainId}
					skill={editingSkill}
				/>
			)}

			<ConfirmDialog
				destructive
				open={!!deletingSkill}
				onClose={() => setDeletingSkill(null)}
				onConfirm={handleDelete}
				title={t("Delete skill")}
				description={t(
					"Are you sure you want to delete this skill? This action cannot be undone.",
				)}
				confirmButtonLabel={t("Delete")}
			/>
		</div>
	);
}
